import { html, LitElement } from 'lit-element';
import 'lm-ui-kit/src/components/uc-header-logo/uc-header-logo.define';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import cn from 'clsx';
import { globalOverlay, findActiveElement } from '../utils';
import { cross } from '../icons/cross';
import '../uc-header-container';
import '../uc-page-content';
import { styles } from './uc-overlay.styles';
import { withEvents } from '../hocs/with-events';
import { handleEscPress } from '../hocs/with-close-on-esc';

export const CLOSE_ACTION = 'close';
export const SCROLL_TOP_ACTION = 'scrolltop';

export class UcOverlay extends LitElement {
  static styles = styles;

  static properties = {
    show: { type: Boolean },
    overlayLevel: { type: Number },
    disableFocusLock: { type: Boolean },

    withoutHeader: { type: Boolean },
    withoutLastElementFocus: { type: Boolean },
    noTransition: {
      type: Boolean,
      reflect: true,
    },
    noScroll: {
      type: Boolean,
      reflect: true,
    },
    closeColor: { type: String, attribute: 'close-color' },
    _show: { type: Boolean },
    _overlayWidth: { type: Number },
  };

  constructor() {
    super();

    this.show = false;
    this.disableFocusLock = false;
    this.overlayLevel = globalOverlay;
    this.withoutHeader = false;
    this.noTransition = false;
    this.noScroll = false;
    this.closeColor = '#a9acb1';

    this._show = this.show;

    this._overlayRef = null;
    this._lastActiveElement = null;
    this.tabIndex = 0;
    this.withoutLastElementFocus = false;
  }

  scrollToTop() {
    if (this._overlayRef) this._overlayRef.scrollTop = 0;
  }

  firstUpdated(_changedProperties) {
    this._overlayRef = this.shadowRoot.querySelector('.js-overlay');
  }

  updated(changes) {
    super.updated(changes);

    if (changes.has('show')) {
      const oldShow = changes.get('show');
      const { show } = this;

      if (show === false && oldShow !== undefined && this._overlayRef && !this.noTransition) {
        this._overlayRef.addEventListener('transitionend', () => this._reflectOuterStateToInnerState(show), {
          once: true,
          passive: true,
        });
      } else {
        this._reflectOuterStateToInnerState(show);
      }
    }

    if (changes.has('_show') && this._show !== undefined && !this.withoutLastElementFocus) {
      if (this._show) {
        this._lastActiveElement = findActiveElement();
        this.focus({ preventScroll: true });
      } else {
        this._lastActiveElement?.focus();
      }
    }

    if (changes.has('closeColor')) {
      this.style.setProperty('--close-button-color', this.closeColor);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this._lockBodyScroll(false);
  }

  render() {
    return html`
      <div
        class=${cn('overlay js-overlay', { show: this.show }, { 'no-scroll': this.noScroll })}
        style="z-index: ${this.overlayLevel}"
      >
        ${this._show
          ? html`
              <div class="container" ?inactive=${this.disableFocusLock}>
                <div class="scroll-compensator">
                  ${!this.withoutHeader
                    ? html`
                        <uc-header-container class="header-wrap">
                          <div slot="left-side" class="title-wrap"><slot name="title"></slot></div>

                          <uc-button
                            slot="right-side"
                            class="close"
                            buttonstyle="text"
                            @click=${this._closeOverlay}
                          >
                            <button type="button" class="close-inner" aria-label="Закрыть модальное окно">
                              <uc-icon class="icon" size="m">${cross}</uc-icon>
                            </button>
                          </uc-button>
                        </uc-header-container>
                      `
                    : html``}
                  <slot class="content"></slot>
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }

  _closeOverlay() {
    this.dispatchEvent(new CustomEvent(CLOSE_ACTION));
  }

  _reflectOuterStateToInnerState = show => {
    if (this._show === show) return;

    this._show = show;

    this._lockBodyScroll(show);
  };

  _lockBodyScroll = show => {
    show
      ? disableBodyScroll(this._overlayRef, { reserveScrollBarGap: true })
      : enableBodyScroll(this._overlayRef);
  };
}

const closeOverlay = (e, thisArg) => {
  const activeElement = findActiveElement();

  if (!(activeElement && 'focusVisibleAdded' in activeElement.dataset)) {
    thisArg._lastActiveElement = null;
  }

  thisArg._closeOverlay();
};

export const EnchancedOverlay = withEvents(UcOverlay, [
  {
    event: 'close-overlay',
    handler: (e, thisArg) => {
      closeOverlay(e, thisArg);
      thisArg.show = false;
    },
    params: { passive: true, element: 'self' },
  },
  {
    event: 'keyup',
    handler: (e, thisArg) => handleEscPress(e, closeOverlay.bind(this, e, thisArg)),
    params: { passive: true },
  },
  {
    event: SCROLL_TOP_ACTION,
    handler: (e, thisArg) => {
      e.stopPropagation();
      if (thisArg._overlayRef) thisArg._overlayRef.scrollTop = 0;
    },
    params: { passive: true, element: 'self' },
  },
]);
